import {
  createContext,
  createRef,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { get as dig, isEqual } from "lodash"
import PropTypes from "prop-types"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { useQuery } from "@tanstack/react-query"
import { LoadingFullScreen } from "source/shared/components"
import { extractMenuItems } from "source/shared/extractMenuItems"
import {
  removeLocalStorageValue,
  useLocalStorage,
} from "source/shared/hooks/useLocalStorage"
import { useShouldSubscribeToPublishingJoltEvents } from "./ReactToPublishingJoltEvents"

const NOOP = () => null
const BOOTING = "BOOTING"

const WEB_BOOT_POLLING_INTERVAL = 5 * 60 * 1000

export const WebBootContext = createContext()

const resetCacheRef = createRef()
resetCacheRef.current = NOOP

export function resetCache() {
  resetCacheRef.current()
}

WebBoot.propTypes = {
  defaultState: PropTypes.object,
  children: PropTypes.node.isRequired,
}
export function WebBoot({ defaultState = "BOOTING", children }) {
  const LOCAL_STORAGE_KEY = "WebBoot.v9"
  const DRAFT_MODE_KEY = "WebBoot.draftMode"
  const navigate = useNavigate()
  const [state, setState] = useLocalStorage(LOCAL_STORAGE_KEY, defaultState)
  const [draftMode, setDraftMode] = useLocalStorage(DRAFT_MODE_KEY, false)
  const mode = draftMode ? "draft" : "published"
  const themeColor = dig(state, `${mode}ThemeSettings.attributes.primary_color`)
  const darkThemeColor = dig(
    state,
    `${mode}ThemeSettings.attributes.dark_primary_color`,
  )
  const themingEnabled = dig(
    state,
    `${mode}ThemeSettings.attributes.theme_enabled`,
  )
  const draftModeRequested = useRef(
    window.location.search.includes("start_draft_mode=1"),
  )

  const canDraftMode = dig(state, "abilities.publishing.draft_mode") || false

  useEffect(() => {
    if (draftModeRequested.current && canDraftMode) {
      setDraftMode(true)
      draftModeRequested.current = false
    }
  }, [canDraftMode])

  // Make setDraftMode available to React Native
  const inReactNativeWebView = !!window.ReactNativeWebView
  useEffect(() => {
    if (inReactNativeWebView) window.setDraftMode = setDraftMode
  }, [inReactNativeWebView, setDraftMode])

  useEffect(() => {
    if (themingEnabled && themeColor) {
      document.body.style.setProperty("--color-brand-light", themeColor)
      document.body.style.setProperty(
        "--color-brand-dark",
        darkThemeColor || themeColor,
      )
      document.body.style.setProperty(
        "--color-brand-gradient-start",
        "var(--color-brand)",
      )
      document.body.style.setProperty(
        "--color-brand-gradient-end",
        "var(--color-brand)",
      )
    }
  }, [themingEnabled, themeColor, darkThemeColor])

  useEffect(() => {
    if (window.location.search.includes("start_draft_mode=1")) {
      let nextUrl = new URL(window.location.toString())
      nextUrl.searchParams.delete("start_draft_mode")
      nextUrl = nextUrl.toString().substring(nextUrl.origin.length) // relative url

      navigate(nextUrl.toString(), {
        state: { ...history.state },
        replace: true,
      })
    }
  }, [draftMode])

  const useJoltToUpdate = useShouldSubscribeToPublishingJoltEvents()

  const { data: webBootData, refetch: refetchWebBoot } = useQuery({
    queryKey: ["webBoot"],
    queryFn: fetchWebBoot,
    refetchInterval: !useJoltToUpdate && WEB_BOOT_POLLING_INTERVAL,
    refetchOnWindowFocus: !useJoltToUpdate,
  })

  useEffect(() => {
    if (webBootData && webBootDataDifferent(state, webBootData))
      setState(webBootData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webBootData])

  // Cleanup the old state shapes
  useEffect(() => {
    removeLocalStorageValue("WebBoot.v1")
    removeLocalStorageValue("WebBoot.v2")
    removeLocalStorageValue("WebBoot.v3")
    removeLocalStorageValue("WebBoot.v4")
    removeLocalStorageValue("WebBoot.v5")
    removeLocalStorageValue("WebBoot.v6")
    removeLocalStorageValue("WebBoot.v7")
    removeLocalStorageValue("WebBoot.v8")
  }, [])

  const resetCache = useCallback(() => {
    removeLocalStorageValue(LOCAL_STORAGE_KEY)
    removeLocalStorageValue(DRAFT_MODE_KEY)
  }, [])

  useEffect(() => {
    resetCacheRef.current = resetCache
    return () => (resetCacheRef.current = NOOP)
  }, [resetCache])

  const reactToCandidateEvents = useCallback(
    (event) => {
      if (!isCustomPageUpdate(event)) refetchWebBoot()
    },
    [refetchWebBoot],
  )

  const contextValue = {
    ...state,
    draftMode,
    reactToCandidateEvents,
    setDraftMode,
  }

  return state === BOOTING ? (
    <LoadingFullScreen />
  ) : (
    <WebBootContext.Provider value={contextValue}>
      {children}
    </WebBootContext.Provider>
  )
}

const isCustomPageUpdate = (event) => {
  return (
    dig(event, "type") === "Page" &&
    dig(event, "relationships.slug.data.id") === "home"
  )
}

function mapApiResponseToStateShape(json) {
  const extractRelationship = (name) => {
    const type = dig(json, `data.relationships.${name}.data.0.type`)
    const id = dig(json, `data.relationships.${name}.data.0.id`)
    return json.included.find((i) => i.type === type && i.id === id)
  }

  // Note: When modifying this object be sure to increment the localStorage cache number and remove the old cache
  return {
    abilities: dig(json, "data.attributes.abilities"),
    analyticsUrl: dig(json, "data.attributes.analytics_url"),
    builtInPages: dig(json, "data.attributes.built_in_pages"),
    currentOrganization: extractRelationship("current_organization"),
    draftHomePage: extractRelationship("draft_home_page"),
    draftMenu: extractRelationship("draft_menu"),
    draftThemeSettings: extractRelationship("draft_theme_settings"),
    features: dig(json, "data.attributes.features"),
    joltWebsocketUrl: dig(json, "data.attributes.jolt_websocket_url"),
    myChurchCenterEnabled: dig(
      json,
      "data.attributes.my_church_center_enabled",
    ),
    publishedHomePage: extractRelationship("published_home_page"),
    publishedMenu: extractRelationship("published_menu"),
    publishedThemeSettings: extractRelationship("published_theme_settings"),
  }
}

function fetchWebBoot() {
  const webBootIncludes = [
    "draft_menu",
    "draft_home_page",
    "draft_theme_settings",
    "published_menu",
    "published_home_page",
    "published_theme_settings",
    "current_organization",
  ]
  const WEB_BOOT_PATH = `/publishing/v2/web_boot?include=${webBootIncludes}`
  return sessionApiClient.get(WEB_BOOT_PATH).then(mapApiResponseToStateShape)
}

const webBootDataDifferent = (prev, next) => {
  if (prev === BOOTING) return true
  const attributesToIgnore = ["draftHomePage", "publishedHomePage"]
  if (prev.draftHomePage?.id !== next.draftHomePage?.id) return true
  if (prev.publishedHomePage?.id !== next.publishedHomePage?.id) return true
  return Object.keys(prev).some((key) => {
    if (attributesToIgnore.includes(key)) return false
    return !isEqual(prev[key], next[key])
  })
}

export function useCustomNavigationLabel(fallback) {
  const state = useContext(WebBootContext)
  const { pathname } = useLocation()
  const menuItems = state.draftMode
    ? extractMenuItems(state.draftMenu)
    : extractMenuItems(state.publishedMenu)

  if (pathname.startsWith("/giving/profile")) {
    return fallback
  }

  const customTitle = menuItems.find((i) =>
    pathname.startsWith(i.href),
  )?.children
  return customTitle || fallback
}
